/**
 * #1331 Part 3 (PR 2) — admin WhatsApp inbox API (/api/v1/admin/whatsapp).
 *
 * apiClient already resolves to the backend envelope `{ success, data }`; `unwrap`
 * reads `.data` exactly once (the recurring double-unwrap bug reads it twice and
 * renders an empty state on a successful 200).
 */
import { apiClient } from './client'
import type { WhatsAppComponents } from '@/utils/whatsappComponents'

export type InboxFilter = 'all' | 'unread' | 'unassigned' | 'archived'
export type InboxEntityType = 'hotel' | 'affiliate'

export interface InboxConversation {
  id: string
  phone: string
  entityType: InboxEntityType | null
  entityId: string | null
  entityName: string | null
  profileName: string | null
  lastPreview: string | null
  lastMessageAt: string
  lastInboundAt: string | null
  unread: boolean
  archived: boolean
  optedOut: boolean
}

export interface InboxConversationDetail extends InboxConversation {
  matchedBy: 'auto' | 'admin' | null
  windowOpen: boolean
  windowClosesAt: string | null
}

export interface InboxMessage {
  id: string
  direction: 'inbound' | 'outbound'
  kind: 'text' | 'media' | 'button' | 'template' | 'event'
  body?: string | null
  mediaType?: string | null
  mediaMime?: string | null
  mediaFilename?: string | null
  mediaStatus?: string | null
  mediaUrl?: string | null
  buttonText?: string | null
  templateSlug?: string | null
  source?: string | null
  deliveryStatus?: string | null
  /** PR 3a: an inbox reply sent by the viewing admin ("You"); other admins' replies are "Team". */
  sentByMe?: boolean
  createdAt: string
}

/** One page of a thread, oldest-first. Pass `nextCursor` back as `before` for the page before it. */
export interface InboxMessagePage {
  items: InboxMessage[]
  hasMore: boolean
  nextCursor: string | null
}

export interface InboxListResult {
  items: InboxConversation[]
  total: number
  page: number
  pageSize: number
}

/** PR 3b: why a template cannot be sent to this thread. */
export type InboxTemplateBlockReason = 'unassigned' | 'no_consent' | 'suppressed'

/** PR 3b: an approved WhatsApp template that fits the thread's partner, prefilled from the partner. */
export interface InboxTemplateOption {
  id: string
  slug: string
  name: string
  metaTemplateName: string | null
  /** Meta's category: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION' (Meta may add more). */
  metaCategory: string | null
  metaLanguage: string | null
  textTemplate: string | null
  whatsappComponents: WhatsAppComponents | null
  variables: Array<{ name: string; position: number | null; value: string }>
}

export interface InboxTemplatePicker {
  eligible: boolean
  reason: InboxTemplateBlockReason | null
  templates: InboxTemplateOption[]
}

/** PR 3c: why a partner cannot be sent an opener. */
export type InboxStartBlockReason = 'no_phone' | 'invalid_phone' | 'no_consent' | 'suppressed' | 'linked_elsewhere'

/** PR 3c: can this hotel/affiliate be messaged, and with which templates. */
export interface InboxStartInfo {
  eligible: boolean
  reason: InboxStartBlockReason | null
  partner: { type: InboxEntityType; id: string; name: string }
  phone: string | null
  existingConversationId: string | null
  linkedTo: { type: InboxEntityType; name: string | null } | null
  templates: InboxTemplateOption[]
}

export interface InboxStartRequest {
  entityType: InboxEntityType
  entityId: string
  templateId: string
  variables: Record<string, string>
}

/** #1331: a shared quick reply (saved text for replies inside the 24 h window). */
export interface QuickReply {
  id: string
  title: string
  body: string
  createdAt: string
  updatedAt: string
}

interface Envelope<T> {
  success?: boolean
  data: T
  error?: string
  message?: string
}

async function unwrap<T>(p: Promise<unknown>): Promise<T> {
  const res = (await p) as Envelope<T>
  if (!res?.success) throw new Error(res?.error || res?.message || 'Request failed')
  return res.data
}

const conv = (id: string) => `/admin/whatsapp/conversations/${encodeURIComponent(id)}`

export const whatsappInboxApi = {
  list: (params: { filter: InboxFilter; q?: string; page?: number }) =>
    unwrap<InboxListResult>(apiClient.get('/admin/whatsapp/conversations', { params })),
  get: (id: string) => unwrap<InboxConversationDetail>(apiClient.get(conv(id))),
  messages: (id: string, before?: string) =>
    unwrap<InboxMessagePage>(apiClient.get(`${conv(id)}/messages`, before ? { params: { before } } : undefined)),
  read: (id: string) => unwrap<{ id: string }>(apiClient.post(`${conv(id)}/read`)),
  archive: (id: string) => unwrap<{ id: string }>(apiClient.post(`${conv(id)}/archive`)),
  unarchive: (id: string) => unwrap<{ id: string }>(apiClient.post(`${conv(id)}/unarchive`)),
  link: (id: string, body: { entityType: InboxEntityType; entityId: string }) =>
    unwrap<{ entityType: InboxEntityType; entityId: string; entityName: string | null }>(apiClient.post(`${conv(id)}/link`, body)),
  rematch: (id: string) => unwrap<{ linked: boolean }>(apiClient.post(`${conv(id)}/rematch`)),
  /** PR 3a: reply inside the 24 h window. One Idempotency-Key per draft: a retry never double-sends. */
  reply: (id: string, text: string, idempotencyKey: string) =>
    unwrap<InboxMessage>(apiClient.post(`${conv(id)}/messages`, { text }, { headers: { 'Idempotency-Key': idempotencyKey } })),
  /** PR 3b: the template picker for a thread (eligibility, reason, fitting templates). */
  templates: (id: string) => unwrap<InboxTemplatePicker>(apiClient.get(`${conv(id)}/templates`)),
  /** PR 3b: send an approved template (allowed once the window has closed). One Idempotency-Key per draft. */
  replyTemplate: (id: string, templateId: string, variables: Record<string, string>, idempotencyKey: string) =>
    unwrap<InboxMessage>(apiClient.post(`${conv(id)}/messages`, { templateId, variables }, { headers: { 'Idempotency-Key': idempotencyKey } })),
  /** PR 3c: can this partner be sent an opener template, and which ones. */
  startInfo: (type: InboxEntityType, id: string) =>
    unwrap<InboxStartInfo>(apiClient.get(`/admin/whatsapp/partners/${type}/${encodeURIComponent(id)}/start`)),
  /** PR 3c: start a conversation with an approved opener. One Idempotency-Key per draft. */
  start: (body: InboxStartRequest, idempotencyKey: string) =>
    unwrap<{ conversationId: string; message: InboxMessage }>(apiClient.post('/admin/whatsapp/conversations', body, { headers: { 'Idempotency-Key': idempotencyKey } })),
  /** #1331: the shared quick replies, alphabetical. */
  quickReplies: () => unwrap<QuickReply[]>(apiClient.get('/admin/whatsapp/quick-replies')),
  createQuickReply: (body: { title: string; body: string }) =>
    unwrap<QuickReply>(apiClient.post('/admin/whatsapp/quick-replies', body)),
  updateQuickReply: (id: string, body: { title: string; body: string }) =>
    unwrap<QuickReply>(apiClient.put(`/admin/whatsapp/quick-replies/${encodeURIComponent(id)}`, body)),
  deleteQuickReply: (id: string) =>
    unwrap<{ id: string }>(apiClient.delete(`/admin/whatsapp/quick-replies/${encodeURIComponent(id)}`)),
  unreadCount: async () => (await unwrap<{ count: number }>(apiClient.get('/admin/whatsapp/unread-count'))).count,
}
